import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@material-ui/core';
import Shadow from '../resources/images/shadow.svg';
import { ContentLayout } from './utilities/layouts/ContentLayout';
import { RequiresLogin } from './utilities/commonUtilities/RequiresLogin';
import AdaptiveNavBar from './utilities/NavBar/AdaptiveNavBar';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FullScreenInputButtonPageStyle } from '../styles/FullScreenButtonPageStyle';

const NonIconSection = ({
    SubtitleText,
    bodyText,
    buttonText,
    onButtonClick,
    showButton = true,
    bodyTestDataAttr,
    stylesOverride,
    loadingButton = false,
    buttonDisabled,
    children,
}) => {
    const classes = FullScreenInputButtonPageStyle(stylesOverride)();

    return (
        <>
            <div className={classes.body}>
                {SubtitleText && <SubtitleText />}
                <Typography data-cy={bodyTestDataAttr} variant="body1" className={classes.bodyText}>
                    {bodyText}
                </Typography>
                {children && children}
                {showButton &&
                    (loadingButton ? (
                        <Button className={clsx(classes.button, classes.loadingButton)} disabled={true}>
                            {<CircularProgress className={classes.loadingIcon} size={14.62} />}
                        </Button>
                    ) : (
                        <Button className={classes.button} disabled={buttonDisabled} onClick={onButtonClick}>
                            {buttonText}
                        </Button>
                    ))}
            </div>
        </>
    );
};

export function FullScreenInputButtonPage({
    isLoginRequired = false,
    Icon,
    ImageTag,
    SubtitleText,
    bodyText,
    buttonText,
    onButtonClick,
    showButton = true,
    showShadow = false,
    bodyTestDataAttr,
    stylesOverride,
    hideNavbar = false,
    buttonDisabled,
    children,
}) {
    if (!isLoginRequired) {
        return (
            <>
                {hideNavbar ? null : <AdaptiveNavBar mobilePresent isErrorPageHeader={true} />}
                <FullScreenButtonPageContent Icon={Icon} ImageTag={ImageTag} showShadow={showShadow}>
                    <NonIconSection
                        stylesOverride={stylesOverride}
                        bodyTestDataAttr={bodyTestDataAttr}
                        SubtitleText={SubtitleText}
                        bodyText={bodyText}
                        buttonText={buttonText}
                        onButtonClick={onButtonClick}
                        showButton={showButton}
                        buttonDisabled={buttonDisabled}
                        children={children}
                    />
                </FullScreenButtonPageContent>
            </>
        );
    } else {
        return (
            <RequiresLogin>
                {hideNavbar ? null : <AdaptiveNavBar mobilePresent isErrorPageHeader={true} />}
                <FullScreenButtonPageContent Icon={Icon} ImageTag={ImageTag} showShadow={showShadow}>
                    <NonIconSection
                        stylesOverride={stylesOverride}
                        bodyTestDataAttr={bodyTestDataAttr}
                        SubtitleText={SubtitleText}
                        bodyText={bodyText}
                        buttonText={buttonText}
                        onButtonClick={onButtonClick}
                        showButton={showButton}
                        buttonDisabled={buttonDisabled}
                        children={children}
                    />
                </FullScreenButtonPageContent>
            </RequiresLogin>
        );
    }
}

/* type checking for props */
FullScreenInputButtonPage.propTypes = {
    isLoginRequired: PropTypes.bool,
    bodyText: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    onButtonClick: PropTypes.func,
    bodyTestDataAttr: PropTypes.string,
    showShadow: PropTypes.bool,
    stylesOverride: PropTypes.any,
};

function FullScreenButtonPageContent({ Icon, ImageTag, showShadow = false, iconTextDataAttr, children, stylesOverride }) {
    const classes = FullScreenInputButtonPageStyle(stylesOverride)();

    return (
        <div className={classes.contentLayoutDiv}>
            <ContentLayout className={classes.fullscreenButtonPageHorizontalPaddingOverride}>
                <Box className={classes.root} display="flex">
                    <Box display="flex" flexDirection="column" className={clsx({ [classes.shadowedIcon]: showShadow })}>
                        {ImageTag && <ImageTag />}
                        {Icon && <Icon data-cy={iconTextDataAttr} aria-labelledby="icon" className={classes.icon} />}
                        {showShadow && <img src={Shadow} alt="shadow" className={classes.shadow} />}
                    </Box>
                    {children}
                </Box>
            </ContentLayout>
        </div>
    );
}
